import { cn } from "@frontend/lib/utils"
import { useEffect, useMemo, useRef, useState } from "react"

interface HistoryTimelineProps {
  checkpointCount: number
  cursor: number
  disabled?: boolean
  onScrub: (index: number) => void
  scrubDebounceMs?: number
  className?: string
}

function clampIndex(index: number, count: number): number {
  if (count <= 0) return 0
  const max = count - 1
  return Math.max(0, Math.min(max, Math.trunc(index)))
}

function toCursorPercent(index: number, count: number): number {
  if (count <= 1) return 0
  return (clampIndex(index, count) / (count - 1)) * 100
}

export function HistoryTimeline({
  checkpointCount,
  cursor,
  disabled = false,
  onScrub,
  scrubDebounceMs = 50,
  className
}: HistoryTimelineProps) {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const draggingRef = useRef(false)
  const lastEmittedIndexRef = useRef<number | null>(null)
  const pendingIndexRef = useRef<number | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const count = Math.max(0, checkpointCount)
  const clampedCursor = clampIndex(cursor, count)
  const [displayCursorIndex, setDisplayCursorIndex] = useState(clampedCursor)
  const cursorPercent = toCursorPercent(displayCursorIndex, count)

  const barPositions = useMemo(() => {
    if (count <= 0) return [] as number[]
    if (count === 1) return [0]
    return Array.from({ length: count }, (_, index) => (index / (count - 1)) * 100)
  }, [count])

  const resolveIndexFromClientX = (clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect || rect.width <= 0) return clampedCursor
    const relative = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    return clampIndex(Math.round(relative * (count - 1)), count)
  }

  const flushPending = () => {
    if (pendingIndexRef.current == null) return
    const pendingIndex = pendingIndexRef.current
    pendingIndexRef.current = null
    if (lastEmittedIndexRef.current === pendingIndex) return
    lastEmittedIndexRef.current = pendingIndex
    onScrub(pendingIndex)
  }

  const emitIndex = (nextIndex: number, immediate = false) => {
    if (disabled || count <= 0) return
    if (lastEmittedIndexRef.current === nextIndex) return
    if (immediate || scrubDebounceMs <= 0) {
      lastEmittedIndexRef.current = nextIndex
      onScrub(nextIndex)
      return
    }

    pendingIndexRef.current = nextIndex
    if (debounceTimerRef.current) return
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null
      flushPending()
    }, scrubDebounceMs)
  }

  const startDrag = (clientX: number) => {
    if (disabled || count <= 0) return
    draggingRef.current = true
    setIsDragging(true)
    pendingIndexRef.current = null
    lastEmittedIndexRef.current = null
    const nextIndex = resolveIndexFromClientX(clientX)
    setDisplayCursorIndex(nextIndex)
    emitIndex(nextIndex, true)
  }

  const updateDrag = (clientX: number) => {
    if (!draggingRef.current) return
    const nextIndex = resolveIndexFromClientX(clientX)
    setDisplayCursorIndex(nextIndex)
    emitIndex(nextIndex)
  }

  const endDrag = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setIsDragging(false)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    flushPending()
    lastEmittedIndexRef.current = null
    pendingIndexRef.current = null
  }

  useEffect(() => {
    if (isDragging) return
    setDisplayCursorIndex(clampedCursor)
  }, [clampedCursor, isDragging])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
    }
  }, [])

  return (
    <div
      ref={trackRef}
      role="slider"
      tabIndex={disabled || count <= 0 ? -1 : 0}
      aria-label="History timeline"
      aria-disabled={disabled || count <= 0}
      aria-valuemin={0}
      aria-valuemax={Math.max(count - 1, 0)}
      aria-valuenow={clampedCursor}
      onKeyDown={(event) => {
        if (disabled || count <= 0) return
        if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
          event.preventDefault()
          emitIndex(clampIndex(clampedCursor - 1, count))
          return
        }
        if (event.key === "ArrowRight" || event.key === "ArrowUp") {
          event.preventDefault()
          emitIndex(clampIndex(clampedCursor + 1, count))
          return
        }
        if (event.key === "Home") {
          event.preventDefault()
          emitIndex(0)
          return
        }
        if (event.key === "End") {
          event.preventDefault()
          emitIndex(count - 1)
        }
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return
        startDrag(event.clientX)
        event.currentTarget.setPointerCapture?.(event.pointerId)
      }}
      onPointerMove={(event) => {
        updateDrag(event.clientX)
      }}
      onPointerUp={(event) => {
        endDrag()
        event.currentTarget.releasePointerCapture?.(event.pointerId)
      }}
      onPointerCancel={() => {
        endDrag()
      }}
      className={cn(
        "relative h-14 rounded-md border border-border bg-black/95 select-none touch-none overflow-hidden",
        disabled || count <= 0 ? "opacity-70 cursor-not-allowed" : "cursor-ew-resize",
        className
      )}
    >
      <div className="absolute inset-0">
        {barPositions.map((position, index) => (
          <span
            // Rendering each checkpoint as a frame bar keeps the timeline literal.
            key={`bar-${index}`}
            data-testid="history-timeline-bar"
            className="absolute top-2 bottom-2 w-px bg-white/90"
            style={{ left: `${position}%`, transform: "translateX(-0.5px)" }}
          />
        ))}
      </div>

      <span
        data-testid="history-timeline-cursor"
        className="absolute top-0 bottom-0 w-0.5 bg-red-500 pointer-events-none shadow-[0_0_8px_rgba(239,68,68,0.7)]"
        style={{ left: `${cursorPercent}%`, transform: "translateX(-0.5px)" }}
      />
    </div>
  )
}
